import { Label } from '@wds/label';
import { createMemoStyles, defaultFonts, deprecate, uniqueId, safeRest } from '@wds/utils';
import React, { useMemo } from 'react';

var COLOR_OPTIONS = [
    'default',
    'success',
    'warning',
    'error',
];

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function _typeof(obj) {
  "@babel/helpers - typeof";

  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    _typeof = function _typeof(obj) {
      return typeof obj;
    };
  } else {
    _typeof = function _typeof(obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
  }

  return _typeof(obj);
}

var SEMANTIC_COLORS = ['primary', 'secondary', 'success', 'warning', 'upgrade', 'error', 'info', 'muted'];
var TYPE_COLORS = ['dark', 'light', 'darkMuted', 'lightMuted', 'link'];
var BACKGROUND_COLORS = ['main', 'light', 'dark', 'overlay', 'accent', 'primary', 'success', 'warning', 'error', 'info', 'upgrade', 'muted'];
var BORDER_COLORS = ['main', 'active', 'hover', 'muted'];
var DATA_COLORS = ['one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten']; /// ///////////////////////// FONT TYPE START ////////////////////////////
var FONT_WEIGHTS = ['light', 'regular', 'medium'];

var dataTransform = function dataTransform(arr) {
  return Object.freeze(arr.map(function (x) {
    return "data-".concat(x);
  }));
};

var DATA_VIS_COLORS = dataTransform(DATA_COLORS);

var getDataColor = function getDataColor(s) {
  return s.split('-')[1];
}; /// ///////////////////////// HELPERS ////////////////////////////


var readOnlyIncludes = function readOnlyIncludes(arr, el) {
  return arr.includes(el);
}; /// ///////////////////////// TYPE GUARDS ////////////////////////////


var isColorShape = function isColorShape(c) {
  return _typeof(c) === 'object' && 'main' in c;
};

var isSemanticColor = function isSemanticColor(c) {
  return readOnlyIncludes(SEMANTIC_COLORS, c);
};

var isLinkColor = function isLinkColor(c) {
  return c === 'link';
};

var isTypeColor = function isTypeColor(c) {
  return readOnlyIncludes(TYPE_COLORS, c);
};

var isBackgroundColor = function isBackgroundColor(c) {
  return readOnlyIncludes(BACKGROUND_COLORS, c);
};

var isBorderColor = function isBorderColor(c) {
  return readOnlyIncludes(BORDER_COLORS, c);
};

var isDataColor = function isDataColor(c) {
  return readOnlyIncludes(DATA_COLORS, c);
};

var isDataVisColor = function isDataVisColor(c) {
  return readOnlyIncludes(DATA_VIS_COLORS, c);
};

var getColor = function getColor(palette, color, _a) {
  var _b = _a === void 0 ? {} : _a,
      _c = _b.semanticKey,
      semanticKey = _c === void 0 ? 'main' : _c,
      _d = _b.colorType,
      colorType = _d === void 0 ? 'semantic' : _d,
      fallback = _b.fallback;

  var c = fallback !== null && fallback !== void 0 ? fallback : 'currentcolor';
  if (!color) return c;
  var cType = colorType; // if passed a portion of the Pallette that is the semantic color use that.

  if (isColorShape(color)) c = color[semanticKey];else {
    // Check Semantic
    if (cType === 'semantic') {
      if (isSemanticColor(color)) c = palette[color][semanticKey];else cType = 'link';
    } // Check type and get Color


    if (cType === 'link') {
      if (isLinkColor(color)) c = palette[color][semanticKey];else cType = 'text';
    }

    if (cType === 'text') {
      if (isTypeColor(color)) c = palette[cType][color];else cType = 'background';
    }

    if (cType === 'background') {
      if (isBackgroundColor(color)) c = palette[cType][color];else cType = 'border';
    }

    if (cType === 'border') {
      if (isBorderColor(color)) c = palette[cType][color];else cType = 'data';
    }

    if (cType === 'data') {
      if (isDataVisColor(color)) c = palette[cType][getDataColor(color)];
      if (isDataColor(color)) c = palette[cType][color];
    }
  }
  return c;
};

process.env.NODE_ENV !== 'production' ? 'cdn.mtassets.net' : 'cdn.smassets.net';
var palette$1 = {
  transparent: 'transparent',
  primary: {
    main: '#00BF6F',
    contrast: '#FFFFFF'
  },
  secondary: {
    main: '#6B787F',
    contrast: '#FFFFFF'
  },
  success: {
    main: '#00BF6F',
    contrast: '#FFFFFF'
  },
  warning: {
    main: '#F05B24',
    contrast: '#FFFFFF'
  },
  error: {
    main: '#F05B24',
    contrast: '#FFFFFF'
  },
  info: {
    main: '#2DCCD3',
    contrast: '#333E48'
  },
  upgrade: {
    main: '#F9BE00',
    contrast: '#333E48'
  },
  muted: {
    main: '#D0D2D3',
    contrast: '#333E48'
  },
  link: {
    main: '#007FAA',
    contrast: '#FFFFFF'
  },
  text: {
    dark: '#333E48',
    light: '#FFFFFF',
    darkMuted: '#6B787F',
    lightMuted: '#D0D2D3',
    link: '#007FAA'
  },
  background: {
    main: '#F7F8FA',
    light: '#FFFFFF',
    dark: '#333E48',
    overlay: 'rgba(107, 120, 127, .86)',
    accent: '#EDEEEE',
    primary: '#E5F9F1',
    success: '#E5F9F1',
    warning: '#FDEBE5',
    error: '#F8ECF2',
    info: '#E6F9FA',
    upgrade: '#FEF2CC',
    muted: '#EDEEEE'
  },
  border: {
    main: '#D0D2D3',
    active: '#404040',
    hover: '#757575',
    muted: '#EDEEEE'
  },
  data: {
    one: '#507CB6',
    two: '#00BF6F',
    three: '#6BC8CD',
    four: '#F9BE00',
    five: '#7D5E90',
    six: '#DB4D5C',
    seven: '#768086',
    eight: '#D25F90',
    nine: '#A38364',
    ten: '#FF8B4F'
  }
};
({
  main: [1, 'solid', getColor(palette$1, 'main', {
    colorType: 'border'
  })],
  hover: [1, 'solid', getColor(palette$1, 'hover', {
    colorType: 'border'
  })],
  active: [1, 'solid', getColor(palette$1, 'active', {
    colorType: 'border'
  })],
  muted: [1, 'solid', getColor(palette$1, 'muted', {
    colorType: 'border'
  })],
  dark: [1, 'solid', getColor(palette$1, 'dark', {
    colorType: 'background'
  })],
  light: [2, 'solid', getColor(palette$1, 'light', {
    colorType: 'background'
  })]
});
process.env.NODE_ENV !== 'production' ? 'cdn.mtassets.net' : 'cdn.smassets.net';
var palette = {
  transparent: 'transparent',
  primary: {
    main: '#008323',
    contrast: '#FFFFFF'
  },
  secondary: {
    main: '#757575',
    contrast: '#FFFFFF'
  },
  success: {
    main: '#008323',
    contrast: '#FFFFFF'
  },
  warning: {
    main: '#F7B500',
    contrast: '#404040'
  },
  error: {
    main: '#CC0000',
    contrast: '#FFFFFF'
  },
  info: {
    main: '#2cb2f1',
    contrast: '#000000'
  },
  upgrade: {
    main: '#ffe11b',
    contrast: '#404040'
  },
  muted: {
    main: '#d0d0d0',
    contrast: '#404040'
  },
  link: {
    main: '#0a7cc1',
    contrast: '#FFFFFF'
  },
  text: {
    dark: '#404040',
    light: '#FFFFFF',
    darkMuted: '#757575',
    lightMuted: '#efefef',
    link: '#0a7cc1'
  },
  background: {
    main: '#f7f7f7',
    light: '#ffffff',
    dark: '#404040',
    overlay: 'rgba(117, 117, 117, .86)',
    accent: '#efefef',
    primary: '#E5F3E9',
    success: '#E5F3E9',
    warning: '#FFEEC0',
    error: '#FBEAEA',
    info: '#DCF0FF',
    upgrade: '#FFF9D1',
    muted: '#EFEFEF'
  },
  border: {
    main: '#d0d0d0',
    active: '#404040',
    hover: '#757575',
    muted: '#EDEEEE'
  },
  data: {
    one: '#507CB6',
    two: '#008323',
    three: '#6BC8CD',
    four: '#F9BE00',
    five: '#7D5E90',
    six: '#DB4D5C',
    seven: '#768086',
    eight: '#D25F90',
    nine: '#A38364',
    ten: '#FF8B4F'
  }
};
getColor(palette, 'main', {
  colorType: 'border'
});
({
  main: [1, 'solid', getColor(palette, 'main', {
    colorType: 'border'
  })],
  hover: [1, 'solid', getColor(palette, 'hover', {
    colorType: 'border'
  })],
  active: [1, 'solid', getColor(palette, 'active', {
    colorType: 'border'
  })],
  muted: [1, 'solid', getColor(palette, 'muted', {
    colorType: 'border'
  })],
  dark: [1, 'solid', getColor(palette, 'dark', {
    colorType: 'background'
  })],
  light: [2, 'solid', getColor(palette, 'light', {
    colorType: 'background'
  })]
});
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

createMemoStyles(function (_a) {
  var _b = _a.type,
      assets = _b.assets,
      fontFamily = _b.fontFamily,
      fontWeight = _b.fontWeight,
      fontSize = _b.fontSize;
  var fontPath = "".concat(assets.base).concat(assets.path);
  var fontFaces = FONT_WEIGHTS.map(function (name) {
    // we need this because the font file names are all National2-<Weight>.2.ext
    var upperCaseName = "".concat(name[0].toUpperCase()).concat(name.slice(1));
    var weight = fontWeight[name];
    return {
      fontFamily: assets.name,
      fontWeight: weight,
      fontDisplay: 'swap',
      src: "url(".concat(fontPath, "-").concat(upperCaseName, ".").concat(assets.version, ".woff2) format('woff2')"),
      fallbacks: [{
        src: "url(".concat(fontPath, "-").concat(upperCaseName, ".").concat(assets.version, ".woff) format('woff')")
      }, {
        src: "url(".concat(fontPath, "-").concat(upperCaseName, ".").concat(assets.version, ".eot#iefix) format('embedded-opentype')")
      }]
    };
  });
  return {
    '@font-face': fontFaces,
    '@global': {
      "[class*='wrench'], [class*='wrench']::before, [class*='wrench']::after": {
        fontFamily: fontFamily.base,
        '-webkit-font-smoothing': 'antialiased',
        fontSize: fontSize.body,
        boxSizing: 'border-box',
        padding: 0,
        margin: 0,
        listStyle: 'none'
      }
    }
  };
});

var useStylesInput = createMemoStyles(function (theme) {
    var radius = theme.radius, palette = theme.palette, shadow = theme.shadow, spacing = theme.spacing, border = theme.border;
    var inputGroupStyles = {
        wrenchInputGroup: function (_a) {
            var color = _a.color, stretched = _a.stretched, disabled = _a.disabled;
            return ({
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: border.main,
                background: palette.background.light,
                borderRadius: radius.small,
                width: stretched ? '100%' : null,
                pointerEvents: disabled ? 'none' : null,
                opacity: disabled ? '0.4' : null,
                borderColor: getColor(palette, color, { fallback: palette.border.main }),
                '&:hover': {
                    borderColor: getColor(palette, color, { fallback: palette.border.hover }),
                    boxShadow: shadow.sky,
                    transition: ['border-color 100ms', 'box-shawdow 100ms'],
                },
                '&:focus': {
                    borderColor: getColor(palette, color, { fallback: palette.border.active }),
                    transition: ['border-color', '100ms'],
                },
                '& > input': {
                    color: getColor(palette, color, { fallback: palette.text.dark }),
                    border: 'none',
                    flexGrow: 1,
                    width: stretched ? '100%' : null,
                    '&:hover': {
                        boxShadow: 'none',
                    },
                },
                '&:empty': {
                    border: 0,
                    clip: 'rect(1px, 1px, 1px, 1px)',
                    height: '1px',
                    margin: '-1px',
                    overflow: 'hidden',
                    padding: 0,
                    position: 'absolute',
                    width: '1px',
                },
            });
        },
        wrenchInputGroupAddon: function () { return (__assign(__assign({ whiteSpace: 'nowrap' }, defaultFonts(theme)), { '&:first-child': {
                paddingLeft: spacing[1],
                '& > svg': {
                    marginLeft: spacing[2],
                    fill: function (_a) {
                        var color = _a.color;
                        return getColor(palette, color, { fallback: palette.text.dark });
                    },
                    color: function (_a) {
                        var color = _a.color;
                        return getColor(palette, color, { fallback: palette.text.dark });
                    },
                },
            }, '&:last-child': {
                paddingRight: spacing[1],
                '& > svg': {
                    marginRight: spacing[2],
                    fill: function (_a) {
                        var color = _a.color;
                        return getColor(palette, color, { fallback: palette.text.dark });
                    },
                    color: function (_a) {
                        var color = _a.color;
                        return getColor(palette, color, { fallback: palette.text.dark });
                    },
                },
            } })); },
    };
    return inputGroupStyles;
});

var InputGroup = function (_a) {
    var children = _a.children, id = _a.id, label = _a.label, _b = _a.stretched, stretched = _b === void 0 ? false : _b, _c = _a.disabled, disabled = _c === void 0 ? false : _c, _d = _a.color, color = _d === void 0 ? 'default' : _d, rest = __rest(_a, ["children", "id", "label", "stretched", "disabled", "color"]);
    deprecate({ color: color }, 'color', 'warning', 'InputGroup');
    var inputID = useMemo(function () { return id !== null && id !== void 0 ? id : uniqueId('input'); }, [id]);
    var wrenchInputGroup = useStylesInput({ color: color, stretched: stretched, disabled: disabled }).wrenchInputGroup;
    return (React.createElement(React.Fragment, null,
        label && React.createElement(Label, { htmlFor: inputID }, label),
        React.createElement("div", __assign({ className: wrenchInputGroup }, safeRest(rest)), children)));
};

var InputGroupAddon = function (_a) {
    var children = _a.children, _b = _a.color, color = _b === void 0 ? 'default' : _b, rest = __rest(_a, ["children", "color"]);
    var wrenchInputGroupAddon = useStylesInput({ color: color }).wrenchInputGroupAddon;
    return (React.createElement("div", __assign({ className: wrenchInputGroupAddon }, safeRest(rest)), children));
};

export { COLOR_OPTIONS, InputGroup, InputGroupAddon };
